import { HttpStatusCode } from '@angular/common/http';
import { Injectable } from '@angular/core';
import {
  EMPTY_STRING,
  ERROR_FETCHING_DIFFERENTIAL_VALUE,
  GET_EXPERIENCE_FRAME,
  GET_METRICS_PATH,
  GET_SENIORITIES_LIST,
  GET_VALUE_DIFFERENTIAL,
  POST_SENIORITY_ASSESSMENT,
  SENIORITY_LIST_PATH,
} from '@app/constants';
import {
  SeniorityAssessmentRequest,
  SeniorityAssessmentResponse,
} from '@app/interface/seniority-assessment.interface';
import { ServiceType } from '@app/interface/service';
import { CardData } from '@interfaces/card-data';
import {
  DifferentialValueResponse,
  ExperienceFrameResponse,
  SeniorityListResponse,
  SeniorityResponse,
  TSeniority,
  TSeniorityLevelMonth,
} from '@interfaces/seniority-information.type';
import { BehaviorSubject, from, Observable } from 'rxjs';
import { doGetResponseType, GatewayService } from './gateway.service';

@Injectable({
  providedIn: 'root',
})
export class SeniorityService {
  enableAssesstment: boolean = false;
  private actualSeniorityName: string = EMPTY_STRING;
  private readonly seniorityCountByYearObservable = new BehaviorSubject<TSeniorityLevelMonth[]>([]);
  private readonly enableAssessmentObservable = new BehaviorSubject<boolean>(false);
  private readonly enableAssessment$ = this.enableAssessmentObservable.asObservable();

  constructor(private readonly gateway: GatewayService) {}

  setActualSeniorityName(name: string): void {
    this.actualSeniorityName = name;
  }

  getActualSeniorityName(): string {
    return this.actualSeniorityName;
  }

  getEnabledAssesstment() {
    return this.enableAssessment$;
  }

  setEnabledAssessment(value: boolean) {
    this.enableAssessmentObservable.next(value);
  }

  getAllSeniorities() {
    return from(
      this.gateway
        .doGet<SeniorityResponse>(GET_SENIORITIES_LIST, {}, ServiceType.AdministrationService)
        .then((response) => {
          if (response.success) {
            const result = response;

            return { ...result, data: result.data as SeniorityResponse[] };
          }
          throw new Error('Failed to fetch Seniorities list');
        })
    );
  }

  getExperienceFrame() {
    return from(
      this.gateway
        .doGet<ExperienceFrameResponse>(GET_EXPERIENCE_FRAME, {}, ServiceType.AdministrationService)
        .then((response) => {
          if (response.success || response.status === HttpStatusCode.Ok) {
            return response.data as ExperienceFrameResponse;
          }
          throw new Error('Failed to fetch Experience Frame');
        })
    );
  }

  getSeniorityList(): Array<TSeniority> {
    return ['chapterTrainee', 'chapterJunior', 'chapterAdvanced', 'chapterSenior', 'chapterMaster'];
  }

  getSeniorityCountObservable() {
    return this.seniorityCountByYearObservable.asObservable();
  }

  async requestSeniorityCountByYear(chapterId: number, startDate: string, endDate: string) {
    return this.gateway
      .doGet('chapter', {
        chapterId,
        startDate,
        endDate,
      })
      .then((response: doGetResponseType) => {
        if (response.success) {
          this.seniorityCountByYearObservable.next(response.data.data);
        } else {
          this.seniorityCountByYearObservable.next([]);
        }

        return response.success;
      });
  }

  getCardData(chapterId: number | string): Observable<CardData> {
    return from(this.getRequestForCardData(chapterId));
  }

  getCardDataPromise(chapterId: number | string | undefined): Promise<CardData> {
    if (chapterId !== undefined) {
      return this.getRequestForCardData(chapterId);
    }

    return Promise.reject(undefined);
  }

  async getRequestForCardData(chapterId: number | string | undefined) {
    if (chapterId !== undefined) {
      return this.gateway
        .doGet(`${GET_METRICS_PATH}${chapterId}`, {}, ServiceType.AdministrationService)
        .then((response: doGetResponseType) => {
          if (chapterId) {
            if (response.success) {
              this.setEnabledAssessment(response.data.data[0].enabledAssessment);
              return response.data;
            } else {
              return {
                message: 'error',
                data: [],
              };
            }
          }
        });
    }
    return Promise.reject(undefined);
  }

  async getSeniorityInformation(chapterId: number, month: number): Promise<doGetResponseType> {
    return this.gateway
      .doGet('chapter', {
        chapterId,
        month,
      })
      .then((response: doGetResponseType) => {
        if (response.success) {
          return response.data;
        } else {
          return {
            message: 'error',
            data: [],
          };
        }
      });
  }

  getSeniorities(page: number = 0, page_size: number = 20): Observable<SeniorityListResponse> {
    const params = { page, page_size };

    return from(
      this.gateway
        .doGet<SeniorityListResponse>(
          SENIORITY_LIST_PATH,
          params,
          ServiceType.AdministrationService
        )
        .then((response) => {
          if (!response.success) {
            throw new Error('Failed to fetch seniorities');
          }
          return response.data as SeniorityListResponse;
        })
    );
  }

  getDifferentialValue(
    chapterId: number,
    specialityId: number,
    seniorityId?: number
  ): Observable<DifferentialValueResponse> {
    const url = GET_VALUE_DIFFERENTIAL;
    const params: Record<string, number> = {
      chapter_id: chapterId,
      speciality_id: specialityId,
    };
    if (typeof seniorityId === 'number') {
      params['seniority_id'] = seniorityId;
    }
    return from(
      this.gateway
        .doGet<DifferentialValueResponse>(url, params, ServiceType.AdministrationService)
        .then((response) => {
          if (response.success) {
            return response.data as DifferentialValueResponse;
          } else {
            throw new Error(ERROR_FETCHING_DIFFERENTIAL_VALUE);
          }
        })
    );
  }

  getSeniorityAssessment(assessmentExecutionId: number): Observable<SeniorityAssessmentResponse> {
    const payload: SeniorityAssessmentRequest = {
      assessment_execution_id: assessmentExecutionId,
    };
    return from(
      this.gateway
        .doPost<SeniorityAssessmentResponse>(
          POST_SENIORITY_ASSESSMENT,
          payload,
          ServiceType.MapRatingService
        )
        .then((response) => {
          return response.data as SeniorityAssessmentResponse;
        })
    );
  }
}
